// The models offered when adding an AI connection, per provider.
//
// A dropdown rather than a free-text box because a mistyped model name fails
// only later, at the first agent run, as an opaque provider error. Typing is
// still allowed: providers ship new models faster than this list is updated,
// and Azure OpenAI deployments carry whatever name the tenant gave them, so a
// fixed list alone would be a cage.
//
// Keep the ids in step with the rate table in the API's aiPricing.js -
// anything missing there is priced at a fallback rate and shown as estimated.

export const PROVIDERS = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'azure_openai', label: 'Azure OpenAI' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'google', label: 'Google (Gemini)' },
  { id: 'qwen', label: 'Qwen (Alibaba Cloud)' },
  { id: 'xai', label: 'xAI (Grok)' },
];

const MODELS = {
  openai: [
    { id: 'gpt-4.1', label: 'GPT-4.1', hint: 'Most capable' },
    { id: 'gpt-4.1-mini', label: 'GPT-4.1 mini', hint: 'Good default' },
    { id: 'gpt-4.1-nano', label: 'GPT-4.1 nano', hint: 'Cheapest' },
    { id: 'gpt-4o', label: 'GPT-4o' },
    { id: 'gpt-4o-mini', label: 'GPT-4o mini' },
    { id: 'o3', label: 'o3', hint: 'Reasoning' },
    { id: 'o4-mini', label: 'o4-mini', hint: 'Reasoning, cheaper' },
  ],
  // An Azure deployment is named by whoever created it, so the list is a
  // starting point and the field stays free-text.
  azure_openai: [
    { id: 'gpt-4.1', label: 'GPT-4.1' },
    { id: 'gpt-4.1-mini', label: 'GPT-4.1 mini' },
    { id: 'gpt-4o', label: 'GPT-4o' },
    { id: 'gpt-4o-mini', label: 'GPT-4o mini' },
  ],
  anthropic: [
    { id: 'claude-fable-5-1', label: 'Claude Fable 5.1', hint: 'Most capable' },
    // Succeeds Opus 5 in the same tier and costs less - $4/$20 per million
    // tokens against $5/$25 - so it takes the default hint from it.
    { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', hint: 'Good default' },
    { id: 'claude-opus-5', label: 'Claude Opus 5' },
    { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', hint: 'Cheaper' },
    { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', hint: 'Fastest' },
    { id: 'claude-opus-4-5', label: 'Claude Opus 4.5' },
    { id: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
  ],
  google: [
    { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', hint: 'Most capable' },
    { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', hint: 'Good default' },
    { id: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite', hint: 'Cheapest' },
    { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
  ],
  qwen: [
    { id: 'qwen-max', label: 'Qwen Max', hint: 'Most capable' },
    { id: 'qwen-plus', label: 'Qwen Plus', hint: 'Good default' },
    { id: 'qwen-turbo', label: 'Qwen Turbo', hint: 'Cheapest' },
  ],
  xai: [
    { id: 'grok-4.7', label: 'Grok 4.7', hint: 'Good default' },
    { id: 'grok-4', label: 'Grok 4' },
  ],
};

export function modelsForProvider(providerType) {
  return MODELS[String(providerType || '').toLowerCase()] || [];
}

export function providerLabel(providerType) {
  const key = String(providerType || '').toLowerCase();
  return PROVIDERS.find((p) => p.id === key)?.label || providerType || '';
}

// Whether the endpoint field applies. Azure needs one because the host is the
// tenant's own resource; Gemini and OpenAI have a fixed default the backend
// supplies, and only need one when routing through a proxy.
export function endpointRequired(providerType) {
  return String(providerType || '').toLowerCase() === 'azure_openai';
}

// Whether the base-URL field applies. Mirrors BASE_URL_PROVIDERS in the API's
// aiConnectionStore.js - a base URL sent for any other provider is dropped on
// save, so offering the field there just invites people to fill in something
// that silently vanishes.
export function supportsBaseUrl(providerType) {
  return ['openai', 'google', 'qwen', 'xai'].includes(String(providerType || '').toLowerCase());
}

export function endpointHint(providerType) {
  const key = String(providerType || '').toLowerCase();
  if (key === 'azure_openai') return 'Required — your Azure OpenAI resource URL.';
  if (key === 'openai') return 'Optional — only for an OpenAI-compatible proxy.';
  if (key === 'google') return 'Optional — defaults to the Gemini OpenAI-compatible endpoint.';
  // The two DashScope regions are separate accounts, not mirrors: a key issued
  // on one is rejected by the other, so anyone on the China site has to change
  // this and it is worth saying where.
  if (key === 'qwen') {
    return 'Optional — defaults to DashScope international. For a mainland China account use https://dashscope.aliyuncs.com/compatible-mode/v1';
  }
  if (key === 'xai') return 'Optional — defaults to https://api.x.ai/v1';
  return 'Optional.';
}

// A model the user typed that isn't on the list is fine, but it is worth
// saying so: it will be priced at a fallback rate and shown as estimated.
export function isKnownModel(providerType, model) {
  const value = String(model || '').trim().toLowerCase();
  if (!value) return true;
  return modelsForProvider(providerType).some((m) => m.id.toLowerCase() === value);
}
